import React from "react"
import { FormattedMessage } from "react-intl"

import { LanzaTable } from "../../../shared_components/widgets/LanzaTable"
import { OperatorInfo } from "../../shared_components_registration"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import SetHeightLoaderFrame from "../../widgets/SetHeightLoaderFrame"
import { LocaleFormattedDate } from "../../internationalization/LocaleFormatted"
import { translation_mapping } from "../../graph_dashboards/ArchivedDashboard"
import { getRunDetailValue, RUN_DETAIL_FIELDS } from "../../../tables/utils"
import { useSensePageWidth } from "../../samplemanagement/SampleScheduleTab/FormSamplePoint"
import { ExperimentDiv, getHighlightedSpans, RunParentDiv } from "../resources/utils"

const HighlightedTextCell = ({ value, row, extra }) => {
  const { original = {} } = row
  const { highlight_words = [] } = original
  const [_, [__, final]] = value
    ? getHighlightedSpans([null, null, value], highlight_words)
    : [null, [null, "-"]]
  return (
    <span>
      {extra && extra}
      {final}
    </span>
  )
}

export default function RunTable({
  intl,
  runs = [],
  loading = false,
  error,
  reloadData,
  live = false,
  setSelectedRun,
  selectedRun,
  actionHeader = {},
  pageSize = 10,
  filters = [],
  showExperiments = false,
  canClickRow = true,
  canTableFilter = false,
}) {
  const pageWidth = useSensePageWidth()

  const getTranslatedKey = (key, isMetadata = false) => {
    if (isMetadata) {
      return intl.formatMessage({ id: `fermmanagement.${key}`, defaultMessage: key })
    } else {
      return intl.formatMessage(translation_mapping[key] || { id: key, defaultMessage: key })
    }
  }

  const handleRowClick = (run) => {
    if (!run?.placeholder && setSelectedRun) {
      setSelectedRun(run?.id === selectedRun?.id ? null : run)
    }
  }

  const isSelectedRun = (run) => run?.id === selectedRun?.id

  const getExpandedRunDetails = (r) => {
    const { metadata = {}, highlight_words = [] } = r
    let expand = false
    const nonMetaFields = RUN_DETAIL_FIELDS.map((detail_type) => [
      detail_type.key,
      getTranslatedKey(detail_type.label),
      getRunDetailValue(r, detail_type),
    ])
    const metaFields = Object.entries(metadata).map(([key, value]) => {
      let val = value
      if (Array.isArray(value)) {
        val = value.join(", ")
      }
      return [key, getTranslatedKey(key, true), val]
    })
    const combinedFields = [...nonMetaFields, ...metaFields].filter((f) => f[2])
    // finalFields defined here so `expand` is updated prior to return
    const finalFields = Object.fromEntries(
      combinedFields.map((f) => {
        const [isExpand, final] = getHighlightedSpans(f, highlight_words, metadata, filters)
        if (isExpand && !expand) {
          expand = true
        }
        return final
      }),
    )
    return [expand, finalFields]
  }

  return (
    <>
      {error ? (
        <PageErrorPanel attemptReload={reloadData} />
      ) : loading ? (
        <SetHeightLoaderFrame height={350} />
      ) : (
        <LanzaTable
          className={"table table-bordered tbl_lanzatable table-hover"}
          columns={[
            {
              Header: <FormattedMessage id={"graphing.run.identifier"} defaultMessage={"Name"} />,
              accessor: "name",
              canFilter: canTableFilter,
              Cell: ({ value, row }) => {
                const { original: run = {} } = row
                return (
                  <HighlightedTextCell
                    value={value}
                    row={row}
                    extra={
                      <>
                        <a target={"_blank"} href={`/performance-analysis/?runs=${run.id}`}>
                          <span className={"fa fa-area-chart text-success"} />
                        </a>
                        <hr style={{ margin: "0" }} />
                        {run?.placeholder && (
                          <>
                            <span
                              title={intl.formatMessage({
                                id: "graphing.run.placeholder",
                                defaultMessage: "Placeholder",
                              })}
                              className="fa fa-bookmark-o text-success"
                            />
                            &nbsp;
                          </>
                        )}
                      </>
                    }
                  />
                )
              },
            },
            {
              Header: <FormattedMessage id={"general.reactor"} defaultMessage={"Reactor"} />,
              accessor: "reactor",
              canFilter: canTableFilter,
              Cell: ({ value, row }) => <HighlightedTextCell value={value} row={row} />,
            },
            {
              Header: <FormattedMessage id={"general.description"} defaultMessage={"Description"} />,
              accessor: "run_description",
              disableSortBy: true,
              hide: pageWidth < 630,
              Cell: ({ value, row }) => <HighlightedTextCell value={value} row={row} />,
            },
            {
              Header: <FormattedMessage id={"general.project.experiment"} defaultMessage={"Experiment"} />,
              accessor: "experiments",
              disableSortBy: true,
              notCentered: true,
              hide: !showExperiments || pageWidth < 900,
              Cell: ({ row = {} }) => {
                const { original: run = {} } = row
                return <ExperimentDiv run={run} />
              },
            },
            {
              Header: <FormattedMessage id={"fermmanagement.started.by"} defaultMessage={"Started By"} />,
              accessor: "started_by",
              disableSortBy: true,
              hide: pageWidth < 1200,
              Cell: ({ value }) => <OperatorInfo op={value} />,
            },
            {
              Header: (
                <FormattedMessage id={"fermmanagement.inoculated.on"} defaultMessage={"Inoculated On"} />
              ),
              accessor: "inoculated_on",
              nonPlaceholder: true,
              Cell: ({ value }) => (value ? <LocaleFormattedDate date={value} /> : <>-</>),
            },
            {
              Header: (
                <FormattedMessage id={"fermmanagement.inoculated.from"} defaultMessage={"Inoculated From"} />
              ),
              id: "inoculated_from",
              nonPlaceholder: true,
              disableSortBy: true,
              notCentered: true,
              hide: pageWidth < 900,
              Cell: ({ row }) => {
                const { original: run = {} } = row
                return <RunParentDiv run={run} />
              },
            },
            {
              Header: <FormattedMessage id={"fermmanagement.ended.by"} defaultMessage={"Ended By"} />,
              accessor: "ended_by",
              archivedOnly: true,
              disableSortBy: true,
              hide: pageWidth < 1200,
              Cell: ({ value }) => <OperatorInfo op={value} />,
            },
            {
              Header: <FormattedMessage id={"fermmanagement.finished.on"} defaultMessage={"Finished On"} />,
              accessor: "finished_on",
              archivedOnly: true,
              Cell: ({ value }) => (value ? <LocaleFormattedDate date={value} /> : <>-</>),
            },
            actionHeader,
          ]
            .filter((h) => (live ? !h?.archivedOnly : h) && !h?.hide && h?.Header)
            .map((h) => (h?.notCentered ? h : { ...h, className: "text-center" }))
            .map((h) => (h?.canFilter ? h : { ...h, disableFilters: true }))}
          data={runs}
          allowSorting={true}
          allowFilters={true}
          allowGlobalFilter={false}
          allowRowSelection={false}
          allowRowExpansion={true}
          getExpandedRowDetails={getExpandedRunDetails}
          onRowClick={canClickRow ? handleRowClick : null}
          isRowSelected={canClickRow ? isSelectedRun : null}
          initialTableOptions={{
            sortBy: [{ id: "started_on", desc: true }],
            pageSize: pageSize,
          }}
        />
      )}
    </>
  )
}
